import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/useCurrentUser";
import { claimsOnlyRedirect } from "@/utils/productRoutes";

interface GateUserState {
  userData?: {
    userEmail?: string | null;
    organization?: { allowed_products?: string[] };
  };
}

/**
 * Sends a claims-only organization to the claims queue from any route it
 * cannot use (#11422): a typed address, a redirectAfterLogin left
 * from an earlier visit, or a page a cached identity from another
 * organization opened. Decided from the products the sign-in answer
 * carries, and re-decided whenever that answer changes; before there is
 * one, nothing is redirected.
 *
 * The identity the page starts with may be a cached one. While the sign-in
 * read is still in flight, a route the cached identity would send away is
 * held blank rather than redirected: a stale claims-only cache must not move
 * a chat organization off its page for good, and a right one must not paint
 * a page the organization cannot use.
 */
const ClaimsOnlyGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const signedIn = useSelector((state: GateUserState) =>
    Boolean(state.userData?.userEmail)
  );
  const products = useSelector(
    (state: GateUserState) => state.userData?.organization?.allowed_products
  );
  const identityReading =
    useIsFetching({ queryKey: CURRENT_USER_QUERY_KEY }) > 0;
  const target =
    signedIn && products ? claimsOnlyRedirect(pathname, products) : null;
  if (target === null) return <>{children}</>;
  if (identityReading) return null;
  return <Navigate to={target} replace />;
};

export default ClaimsOnlyGate;
